import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { BRAND } from "../content";
import { Phone, Mail, MapPin, Send, Calendar } from "lucide-react";
import CalendlyButton from "./CalendlyButton";
import { openWhatsApp, buildWhatsAppUrl } from "../lib/whatsapp";
import { trackLead } from "../lib/analytics";

const GOAL_OPTIONS = [
  "Fat Loss",
  "PCOS / Hormonal",
  "Thyroid",
  "Diabetes",
  "Therapeutic Diet",
  "Mental Wellness",
  "General Lifestyle",
];

export default function Booking() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    goal: GOAL_OPTIONS[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/leads`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json().catch(() => ({}));
      trackLead({ goal: form.goal, leadId: data?.id });
      toast.success(
        "Thank you! Dt. Pooja's team will reach out within 24 hours.",
        {
          description: "Your consultation request has been received.",
        },
      );
      setForm({
        name: "",
        phone: "",
        email: "",
        goal: GOAL_OPTIONS[0],
        message: "",
      });
    } catch (err) {
      toast.error("Couldn't submit just now. Please WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative section-pad"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <div className="text-[#27C6FF] uppercase tracking-[0.3em] text-xs mb-5 font-heading">
            Book a Consultation
          </div>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
            Your transformation
            <br />
            <span className="gradient-text">starts with one call.</span>
          </h2>
          <p className="mt-6 text-[#D8CCFF] text-base md:text-lg font-body">
            Share a few details and our team will reach out within 24 hours to
            schedule your consultation.
          </p>

          <div className="mt-6 glass rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7A2BFF] to-[#27C6FF] flex items-center justify-center flex-shrink-0">
              <Calendar size={18} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="text-white font-heading font-semibold text-sm">
                Prefer to pick a slot directly?
              </div>
              <div className="text-[#CFAEFF] text-xs mt-1">
                See live availability and book in seconds.
              </div>
            </div>
            <CalendlyButton
              variant="primary"
              testId="booking-calendly"
              label="Open Calendar"
              className="!px-5 !py-2.5 !text-sm"
            />
          </div>

          <div className="mt-6 space-y-4">
            <a
              href={buildWhatsAppUrl(BRAND.whatsapp, "Hi DietDotIn, I'd like to book a consultation.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.preventDefault();
                openWhatsApp({
                  phone: BRAND.whatsapp,
                  message: "Hi DietDotIn, I'd like to book a consultation.",
                  displayNumber: BRAND.whatsappDisplay,
                });
              }}
              data-testid="booking-whatsapp-link"
              className="flex items-center gap-4 glass rounded-2xl p-4 hover:neon-aqua transition-shadow"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7A2BFF] to-[#27C6FF] flex items-center justify-center">
                <Phone size={18} className="text-white" />
              </div>
              <div>
                <div className="text-white font-heading font-semibold text-sm">
                  WhatsApp
                </div>
                <div className="text-[#CFAEFF] text-xs">
                  {BRAND.whatsappDisplay}
                </div>
              </div>
            </a>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="booking-email-link"
              className="flex items-center gap-4 glass rounded-2xl p-4 hover:neon-violet transition-shadow"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7A2BFF] to-[#27C6FF] flex items-center justify-center">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <div className="text-white font-heading font-semibold text-sm">
                  Email
                </div>
                <div className="text-[#CFAEFF] text-xs">{BRAND.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-4 glass rounded-2xl p-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#7A2BFF] to-[#27C6FF] flex items-center justify-center">
                <MapPin size={18} className="text-white" />
              </div>
              <div>
                <div className="text-white font-heading font-semibold text-sm">
                  Online & In-Person
                </div>
                <div className="text-[#CFAEFF] text-xs">{BRAND.city}</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7 glass-strong rounded-3xl p-6 md:p-10"
          data-testid="booking-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
                Full Name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Aanya Sharma"
                className="field mt-2 w-full px-4 py-3 rounded-xl text-sm md:text-base"
                data-testid="form-name"
              />
            </div>
            <div>
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
                Phone / WhatsApp *
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
                placeholder="+91 98XXXXXXXX"
                className="field mt-2 w-full px-4 py-3 rounded-xl text-sm md:text-base"
                data-testid="form-phone"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                placeholder="you@email.com"
                className="field mt-2 w-full px-4 py-3 rounded-xl text-sm md:text-base"
                data-testid="form-email"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
                Primary Goal
              </label>
              <select
                value={form.goal}
                onChange={(e) => update("goal", e.target.value)}
                className="field mt-2 w-full px-4 py-3 rounded-xl text-sm md:text-base"
                data-testid="form-goal"
              >
                {GOAL_OPTIONS.map((g) => (
                  <option key={g} value={g} className="bg-[#140326]">
                    {g}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[#CFAEFF] text-xs font-heading uppercase tracking-wider">
                A bit about you (optional)
              </label>
              <textarea
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                rows={4}
                placeholder="Anything we should know? Existing conditions, past plans tried, expectations…"
                className="field mt-2 w-full px-4 py-3 rounded-xl text-sm md:text-base resize-none"
                data-testid="form-message"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="form-submit"
            className="btn-primary mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-heading font-semibold disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Request Consultation"}
            <Send size={16} />
          </button>
          <p className="mt-4 text-[10px] md:text-xs text-[#CFAEFF]">
            By submitting, you agree to be contacted regarding your enquiry.
            We never share your information.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
